import type { INetworkQualityOption } from './options'
import type { IAudioStats, IVideoStats } from './statsProcess'

const QoELevel = {
  INVALID: 0,
  GOOD: 1, // 好
  MEDIUM: 2, // 中, 感知有卡顿和延迟, 发送码率不足
  POOR: 3 // 差, 卡顿和延迟比较大, 影响到沟通
}

const QosLevel = {
  UNKOWN: 0, // 无效值
  EXCELLENT: 1, // 最好
  GOOD: 2, // 好
  POOR: 3, // 中等
  BAD: 4, // 差
  VBAD: 5 // 最差
}

/**
 * @brief 网络质量
 */
const Quality = {
  /**
   * @brief 网络质量未知
   */
  UNKNOWN: 0,
  /**
   * @brief 网络质量极好
   */
  EXCELLENT: 1,
  /**
   * @brief 主观感觉和 Excellent 差不多，但码率可能略低
   */
  GOOD: 2,
  /**
   * @brief 主观感受有瑕疵但不影响沟通
   */
  POOR: 3,
  /**
   * @brief 勉强能沟通但不顺畅。建议降低采样率、码率或通过 UI 提示用户是否切换为纯语音通话。
   */
  BAD: 4,
  /**
   * @brief 网络质量非常差，基本不能沟通。建议通过 UI 给出弱网提示。
   */
  VBAD: 5,
  /**
   * @brief 网络连接断开，无法通话。
   */
  DOWN: 6
}

export interface INetworkQualityDetail {
  qualityQos: number
  audioLevel: number
  videoLevel: number
  audioQos: number
  audioQoe: number
  videoQos: number
  videoQoe: number
}

export default class NetworkQuality {
  /** 保存4s，即两次网络评分。后续上报网络评分时取好的评分进行上报 */
  private _preDownlinkStats: number[] = []

  /**
   * @param {RTCPeerConnection} peer
   */
  constructor(
    private _peer: RTCPeerConnection,
    private _networkQualityParams: INetworkQualityOption
  ) {}

  public getDownlinkQuality(
    audioStats: IAudioStats & { stallDuration: number },
    videoStats: IVideoStats & { stallDuration: number }
  ): [number, Partial<INetworkQualityDetail>] | undefined {
    if (audioStats.receivedKBitrate === 0 && videoStats.receivedKBitrate === 0) {
      // 如果对方码率同时为0，有可能是突然断线断网，不纳入统计
      return
    }

    // 网络断开，上下行网络为极差
    // window firefox 是 connecting
    if (
      (this._peer.connectionState !== 'connected' &&
        this._peer.connectionState !== 'connecting') ||
      !navigator.onLine
    ) {
      return [Quality.DOWN, {}]
    }

    // 远端音频缺失重传率统计，暂时共用视频的丢包率
    const audioQos = this._getQosLevel(
      audioStats.rtt,
      audioStats.audioLossRate,
      videoStats.retransmittedRate
    )
    const audioQoe = this._getDownlinkQoE(
      'audio',
      audioStats.stallDuration / audioStats.statsInterval,
      audioStats.e2eDelay
    )
    const audioLevel = this._getNetworkQuality(audioQos, audioQoe)
    const videoQos = this._getQosLevel(
      videoStats.rtt,
      videoStats.videoLossRate,
      videoStats.retransmittedRate
    )
    const videoQoe = this._getDownlinkQoE(
      'video',
      videoStats.stallDuration / videoStats.statsInterval,
      videoStats.e2eDelay
    )
    const videoLevel = this._getNetworkQuality(videoQos, videoQoe)

    const quality =
      audioLevel && videoLevel
        ? Math.ceil((audioLevel + videoLevel) / 2)
        : audioLevel || videoLevel

    const qualityQos =
      audioQos && videoQos ? Math.ceil((audioQos + videoQos) / 2) : audioQos || videoQos

    // console.log('audioLevel:', audioLevel, ', Qos:', audioQos, ', Qoe:', audioQoe, ', rtt:', audioStats.rtt, ', audioLossRate:', audioStats.audioLossRate, ', stallDuration:', audioStats.stallDuration, ', e2eDelay:', audioStats.e2eDelay)
    // console.log('videoLevel:', videoLevel, ', Qos', videoQos, ', Qoe', videoQoe,  ', rtt:', videoStats.rtt, ', videoLossRate:', videoStats.videoLossRate, ', stallDuration', videoStats.stallDuration, ', e2eDelay:', videoStats.e2eDelay, ', retransmittedRate:', videoStats.retransmittedRate)
    // console.log('quality:', quality)
    if (quality) {
      this._preDownlinkStats = [...this._preDownlinkStats, quality].slice(-2)
      return [
        Math.floor(
          this._preDownlinkStats.reduce((res, cur) => cur + res, 0) /
            this._preDownlinkStats.length
        ),
        {
          qualityQos,
          audioLevel,
          videoLevel,
          audioQos,
          audioQoe,
          videoQos,
          videoQoe
        }
      ]
    }
  }

  destroy() {
    this._preDownlinkStats = []
  }

  /**
   * 计算QoS，由rtt和原始丢包率计算，重传率矫正
   * @param {number} rtt rtt
   * @param {number} loss 丢包率
   * @param {number} retransmittedRate 重传率
   * @returns {number}
   */
  _getQosLevel(rtt: number, loss: number, retransmittedRate: number) {
    let quality

    const qosQualityParams = this._networkQualityParams.networkQos

    // 根据rtt和loss计算QoS
    if (
      (!rtt || rtt <= qosQualityParams[QosLevel.EXCELLENT].maxRtt) &&
      loss <= qosQualityParams[QosLevel.EXCELLENT].maxLoss
    ) {
      quality = QosLevel.EXCELLENT
    } else if (
      (!rtt || rtt <= qosQualityParams[QosLevel.GOOD].maxRtt) &&
      loss <= qosQualityParams[QosLevel.GOOD].maxLoss
    ) {
      quality = QosLevel.GOOD
    } else if (
      (!rtt || rtt <= qosQualityParams[QosLevel.POOR].maxRtt) &&
      loss <= qosQualityParams[QosLevel.POOR].maxLoss
    ) {
      quality = QosLevel.POOR
    } else if (
      (!rtt || rtt <= qosQualityParams[QosLevel.BAD].maxRtt) &&
      loss <= qosQualityParams[QosLevel.BAD].maxLoss
    ) {
      quality = QosLevel.BAD
    } else {
      quality = QosLevel.VBAD
    }

    // 通过重传率设定QoS上限
    if (
      retransmittedRate > (qosQualityParams[QosLevel.BAD].minRetransmittedRate || 0.5)
    ) {
      quality = Math.max(quality, QosLevel.BAD)
    } else if (
      retransmittedRate > (qosQualityParams[QosLevel.POOR].minRetransmittedRate || 0.35)
    ) {
      quality = Math.max(quality, QosLevel.POOR)
    } else if (
      retransmittedRate > (qosQualityParams[QosLevel.GOOD].minRetransmittedRate || 0.2)
    ) {
      quality = Math.max(quality, QosLevel.GOOD)
    }

    return quality
  }

  /**
   * 计算下行QoE，由卡顿率和e2e延迟计算
   * @param {'video' | 'audio'} streamType 流类型
   * @param {number} stallRadio 卡顿率
   * @param {number} delay e2e延迟
   * @returns {number} QoELevel
   */
  _getDownlinkQoE(
    streamType: 'video' | 'audio',
    stallRadio: number,
    delay: number
  ): number {
    const qoeQualityParams = this._networkQualityParams.networkQoe

    const conf = {
      audio: {
        delay: qoeQualityParams.audioDelay,
        stallRadio: qoeQualityParams.audioStallRadio
      },
      video: {
        delay: qoeQualityParams.videoDelay,
        stallRadio: qoeQualityParams.videoStallRadio
      }
    }[streamType]

    // POOR: 卡顿率大于1倍阈值 OR e2e延迟大于1倍阈值 OR (卡顿率大于0.5倍阈值 AND e2e延迟大于0.5倍阈值)
    // MEDIUM: 卡顿率大于0.5倍阈值 OR e2e延迟大于0.5倍阈值
    // GOOD: 卡顿率和e2e延迟都小于0.5倍阈值
    if (
      stallRadio > conf.stallRadio ||
      delay > conf.delay ||
      (stallRadio > conf.stallRadio / 2 && delay > conf.delay / 2)
    ) {
      return QoELevel.POOR
    } else if (stallRadio > conf.stallRadio / 2 || delay > conf.delay / 2) {
      return QoELevel.MEDIUM
    } else {
      // return QoELevel.GOOD
      // web实际测试下来，QOE为GOOD会导致计算不准。暂时返回MEDIUM
      return QoELevel.MEDIUM
    }
  }

  /**
   * 根据QoS和QoE计算网络评分
   * QoE用于修正QoS
   * @param {number} QoS
   * @param {number} QoE
   * @returns {number}
   */
  _getNetworkQuality(QoS: number, QoE: number): number {
    if (QoE === QoELevel.GOOD) {
      return Math.max(QoS - 2, 1)
    } else if (QoE === QoELevel.MEDIUM) {
      return QoS
    } else {
      return Math.min(QoS + 1, 5)
    }
  }
}
